import { debounce } from 'throttle-debounce';
// import TweenLite from 'gsap/TweenLite';
import Presentation from './Presentation';
import { breakpointsNames, breakpointsMediaQueries } from './breakpoints';
import wheelControl from './control/wheelControl';
import touchControl from './control/touchControl';
import keyboardControl from './control/keyboardControl';
import fontsLoaded from './fontsLoaded';

class Presentations {
  constructor() {
    this.currentPresentationElement = null;
    this.presentation = null;
    this.images = {};
    this.ignoreResize = false;
    this.fadePromise = Promise.resolve();

    fontsLoaded()
      .then(() => {
        this.createPresentation();
        // this.presentation.seek(''); // index or id (for development)

        this.setupResize();
        this.indexImages();

        // document.body.style.visibility = 'inherit';
        // return Promise.resolve();
        // eslint-disable-next-line
        return this.presentation.playStartTimeline();
      })
      .then(() => {
        Presentations.addRecaptcha();
        this.downloadImages();
        wheelControl(this);
        touchControl(this);
        keyboardControl(this);
      });
  }

  setupResize() {
    const debouncedResize = debounce(50, () => this.reset());

    window.addEventListener('resize', () => {
      if (this.ignoreResize) {
        return;
      }

      debouncedResize();
    });

    const fullScreenListener = () => {
      if (document.fullscreenElement) {
        this.ignoreResize = true;
      } else {
        setTimeout(() => {
          this.ignoreResize = false;
        }, 100);
      }
    };

    document.addEventListener('fullscreenchange', fullScreenListener);
    document.addEventListener('webkitfullscreenchange', fullScreenListener);
  }

  static addRecaptcha() {
    const script = document.createElement('script');
    script.src =
      'https://www.google.com/recaptcha/api.js?onload=recaptchaLoaded&render=explicit';
    script.async = true;

    document.head.appendChild(script);
  }

  createPresentation() {
    this.presentation = new Presentation(
      this,
      this.clonePresentation(),
      Presentations.currentBreakpoint(),
    );
  }

  clonePresentation() {
    const basePresentation = document.querySelector('.js-presentation');
    basePresentation.style.display = 'none';
    const cloned = basePresentation.cloneNode(true);
    cloned.classList.remove('js-presentation');
    cloned.style.display = '';
    basePresentation.parentElement.appendChild(cloned);
    this.currentPresentationElement = cloned;
    return cloned;
  }

  static currentBreakpoint() {
    let previousName = '';
    for (let i = 0; i < breakpointsNames.length; i += 1) {
      const bpName = breakpointsNames[i];
      if (breakpointsMediaQueries[bpName].matches) {
        previousName = bpName;
      } else {
        break;
      }
    }
    return previousName;
  }

  reset() {
    const current = this.currentPresentationElement;
    const currentParent = current.parentElement;
    currentParent.removeChild(current);
    const { currentId } = this.presentation;
    this.createPresentation();
    this.presentation.seek(currentId);
    this.downloadImages();
  }

  fade(id) {
    let resolveFadePromise;
    this.fadePromise = new Promise(res => {
      resolveFadePromise = res;
    });

    const oldElement = this.currentPresentationElement;
    const parent = oldElement.parentElement;
    this.createPresentation();
    this.presentation.seek(id);
    this.downloadImages();
    const newElement = this.currentPresentationElement;
    newElement.style.opacity = '0';
    newElement.style.zIndex = '2';

    // eslint-disable-next-line no-unused-expressions
    window.getComputedStyle(newElement).transform;

    newElement.classList.add('animate');
    newElement.style.opacity = '1';

    setTimeout(() => {
      newElement.style.zIndex = '';
      newElement.classList.remove('animate');
      newElement.style.opacity = '';
      parent.removeChild(oldElement);
      resolveFadePromise();
    }, 501);
  }

  /* eslint-disable no-param-reassign */
  indexImages() {
    const basePresentation = document.querySelector('.js-presentation');
    const { images } = this;
    let id = 1;

    const getId = () => {
      id += 1;
      return id;
    };

    [
      ...basePresentation.querySelectorAll('[data-lazy-image]'),
      ...this.currentPresentationElement.querySelectorAll('[data-lazy-image]'),
    ].forEach(el => {
      const data = el
        .getAttribute('data-lazy-image')
        .split(',')
        .map(part => part.trim());
      const url = data.pop();
      data.shift(); // where
      const when = data.shift();

      // console.log(el);

      if (images[url]) {
        el.setAttribute('data-lazy-image-id', images[url].id);
        images[url].when.add(when);
        return;
      }

      images[url] = {
        status: 'ADDED',
        id: getId(),
        when: new Set([when]),
      };
      const image = images[url];

      el.setAttribute('data-lazy-image-id', image.id);
    });
  }

  downloadImages() {
    const { presentation, images } = this;
    const { currentsStepsIds, currentId } = presentation;
    const currentIndex = currentsStepsIds.indexOf(currentId);
    const upToCurrent =
      currentIndex > -1 ? currentsStepsIds.slice(0, currentIndex + 1) : [];
    const allowed = ['start', ...upToCurrent];
    // console.log(allowed);

    Object.entries(images).forEach(([url, image]) => {
      if (
        !(
          image.status === 'ADDED' &&
          [...image.when].some(el => allowed.includes(el))
        )
      ) {
        return;
      }

      // console.log('downloading', image);

      image.status = 'DOWNLOADING';

      if (url.startsWith('http')) {
        setTimeout(() => {
          image.status = 'DONE';
          image.url = url;
          this.applyImage(image);
        });
        return;
      }

      const request = new XMLHttpRequest();
      request.open('get', url, true);
      request.responseType = 'blob';

      request.onload = () => {
        if (request.status !== 200) {
          image.status = 'ERROR';
          return;
        }

        const blob = request.response;
        const blobUrl = URL.createObjectURL(blob);
        image.status = 'DONE';
        image.url = blobUrl;
        this.applyImage(image);
      };

      request.onerror = () => {
        image.status = 'ERROR';
      };

      request.send(null);
    });
  }

  applyImage(image) {
    const basePresentation = document.querySelector('.js-presentation');
    const query = `[data-lazy-image-id="${image.id}"]`;
    [
      ...basePresentation.querySelectorAll(query),
      ...this.currentPresentationElement.querySelectorAll(query),
    ].forEach(el => {
      const data = el
        .getAttribute('data-lazy-image')
        .split(',')
        .map(part => part.trim());
      const where = data.shift();
      // const when = data.shift();
      el.setAttribute('data-lazy-image-id', '');
      if (where === 'background') {
        el.style.backgroundImage = `url('${image.url}')`;
      } else if (where === 'src') {
        el.src = image.url;
      } else if (where === 'poster') {
        el.poster = image.url;
      } else if (where === 'href') {
        el.setAttributeNS(
          'http://www.w3.org/1999/xlink',
          'xlink:href',
          image.url,
        );

        // Make image visible for a moment because safari :()
        const style = window.getComputedStyle(el);
        if (style.opacity === '0') {
          const saved = el.style.opacity;

          el.style.opacity = '1';
          // eslint-disable-next-line no-unused-expressions
          window.getComputedStyle(el).transform;
          el.style.opacity = saved || '';
        }
      }
    });
  }
  /* eslint-enable no-param-reassign */
}

export default () => new Presentations();



// WEBPACK FOOTER //
// ./src/scripts/modules/Presentations.js